import { Box, Button, Checkbox, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Typography } from "@mui/material";
import { useState } from "react";
import { ToastContent } from "../../../shared/components/ToastContent";
import { useToast } from "../../../shared/hooks";
import { returnMessageError } from "../../../shared/utils/returnMessageError";
import { useHandleNfse } from "../hooks/useHandleNfse";
import { NFMenu } from "./Menus/NFMenu";
import { ModalCancelNfse } from "./Modals/ModalCancelNfse";
import { TableNfseRow } from "./TableNfseRow";

interface Props {
	rows: any[];
	page: number;
	rowsPerPage: number;
	totalCount: number;
	isLoading?: boolean;
	generalCheckboxSelected: boolean;
	selectedRows: string[];
	onPageChange: (newPage: number) => void;
	onRowsPerPageChange: (value: number) => void;
	onToggleGeneralCheckbox: (checked: boolean) => void;
	isNFSelectable: (row: any) => boolean;
	onToggleRowCheckbox: (id: string) => void;
	currentStatus: string;
}

export function TableNfse({
	rows,
	page,
	rowsPerPage,
	totalCount,
	isLoading,
	generalCheckboxSelected,
	selectedRows,
	onPageChange,
	onRowsPerPageChange,
	onToggleGeneralCheckbox,
	onToggleRowCheckbox,
	isNFSelectable,
	currentStatus,
}: Props) {
	const toastHandler = useToast();
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const [selectedRow, setSelectedRow] = useState<any>(null);
	const [openCancelModal, setOpenCancelModal] = useState(false);
	const { handleDownloadNfsePDF, handleUpdateStatusNfse, handleGenerateNfse, handleReprocessNfse } = useHandleNfse();
	const [isEmitting, setIsEmitting] = useState(false);
	const [isReprocessing, setIsReprocessing] = useState(false);
	const [batchProgress, setBatchProgress] = useState<{
		open: boolean;
		current: number;
		total: number;
		label: string;
	}>({ open: false, current: 0, total: 0, label: "" });

	const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>, row: any) => {
		setAnchorEl(event.currentTarget);
		setSelectedRow(row);
	};

	const handleCloseMenu = () => {
		setAnchorEl(null);
	};

	const refreshList = () => {
		setTimeout(() => {
			onPageChange(page);
		}, 300);
	};

	const handleEmitSelected = async () => {
		setIsEmitting(true);

		const rowsToEmit = rows.filter((r) => selectedRows.includes(r.Installment_id) && r.Installment_status === "paid" && (!r.nfse_id || r.nfse_id === ""));

		setBatchProgress({ open: true, current: 0, total: rowsToEmit.length, label: "Emitindo nota" });

		for (let i = 0; i < rowsToEmit.length; i++) {
			const r = rowsToEmit[i];

			setBatchProgress((prev) => ({ ...prev, current: i + 1 }));

			try {
				await handleGenerateNfse(r.Installment_id);
			} catch (error) {
				console.error(`Erro ao gerar NFSe para ${r.Installment_id}`, error);
			}

			await new Promise((resolve) => setTimeout(resolve, 500));
		}

		setBatchProgress({ open: false, current: 0, total: 0, label: "" });
		setIsEmitting(false);
		refreshList();
		onToggleGeneralCheckbox(false);
	};

	const handleReprocessSelected = async () => {
		setIsReprocessing(true);

		const rowsToReprocess = rows.filter(
			(r) =>
				selectedRows.includes(r.Installment_id) &&
				r.nfse_status === "unprocessed" &&
				Boolean(r.nfse_id) &&
				r.nfse_gateway === "GRIDWEBTI"
		);

		setBatchProgress({ open: true, current: 0, total: rowsToReprocess.length, label: "Reprocessando nota" });

		let successCount = 0;
		let failureCount = 0;

		for (let i = 0; i < rowsToReprocess.length; i++) {
			const row = rowsToReprocess[i];

			setBatchProgress((prev) => ({ ...prev, current: i + 1 }));

			try {
				await handleReprocessNfse(row);
				successCount += 1;
			} catch (error: any) {
				failureCount += 1;
				console.error(`Erro ao reprocessar NFSe ${row.Installment_id}`, error);
			}

			await new Promise((resolve) => setTimeout(resolve, 500));
		}

		setBatchProgress({ open: false, current: 0, total: 0, label: "" });
		setIsReprocessing(false);
		refreshList();
		onToggleGeneralCheckbox(false);

		if (failureCount === 0) {
			toastHandler.present({
				type: "success",
				position: "top-right",
				messageOrContent: <ToastContent title="Sucesso" content={`${successCount} nota(s) reprocessada(s) com sucesso`} />,
			});
			return;
		}

		toastHandler.present({
			type: failureCount === rowsToReprocess.length ? "error" : "warning",
			position: "top-right",
			messageOrContent: (
				<ToastContent
					title={failureCount === rowsToReprocess.length ? "Erro" : "Atenção"}
					content={
						failureCount === rowsToReprocess.length
							? "Não foi possível reprocessar as notas selecionadas"
							: `${successCount} nota(s) reprocessada(s) e ${failureCount} com falha`
					}
				/>
			),
		});
	};

	const hasSelectedToEmit = rows.some((r) => selectedRows.includes(r.Installment_id) && r.Installment_status === "paid" && (!r.nfse_id || r.nfse_id === ""));
	const hasSelectedToReprocess = rows.some(
		(r) =>
			selectedRows.includes(r.Installment_id) &&
			r.nfse_status === "unprocessed" &&
			Boolean(r.nfse_id) &&
			r.nfse_gateway === "GRIDWEBTI"
	);

	return (
		<>
			{batchProgress.open && (
				<Box
					sx={{
						position: "fixed",
						top: 0,
						left: 0,
						width: "100vw",
						height: "100vh",
						bgcolor: "rgba(0, 0, 0, 0.5)",
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						zIndex: 1300,
					}}
				>
					<Paper sx={{ p: 4, borderRadius: 2 }}>
						<Typography variant="h6">
							{batchProgress.label} {batchProgress.current} de {batchProgress.total}...
						</Typography>
					</Paper>
				</Box>
			)}

			<Box display="flex" justifyContent="flex-end" alignItems="center" gap={1} mt={2} mb={1} pr={2}>
				{currentStatus === "unprocessed" && (
					<Button variant="outlined" onClick={handleReprocessSelected} disabled={isEmitting || isReprocessing || !hasSelectedToReprocess}>
						{isReprocessing ? "Reprocessando..." : "Reprocessar Selecionadas"}
					</Button>
				)}
				<Button
					variant="contained"
					onClick={handleEmitSelected}
					disabled={isEmitting || isReprocessing || !hasSelectedToEmit}
				>
					{isEmitting ? "Emitindo..." : "Emitir Notas Selecionadas"}
				</Button>
			</Box>

			<TableContainer
				component={Paper}
				variant="elevation"
				sx={{
					mt: 1,
					width: "96%",
					px: { xs: 1, sm: 3 },
					py: { xs: 2, sm: 3 },
					ml: 1,
					borderRadius: 2,
					overflow: "hidden",
				}}
			>
				<Table size="small" stickyHeader>
					<TableHead>
						<TableRow sx={{ backgroundColor: "grey.100" }}>
							<TableCell padding="checkbox">
								<Checkbox checked={generalCheckboxSelected} onChange={(e) => onToggleGeneralCheckbox(e.target.checked)} />
							</TableCell>
							{["Cliente", "Parceiro", "Endereço", "Vencimento", "Status", "Valor", "Ações"].map((header, i) => (
								<TableCell key={i} align={i === 6 ? "right" : i === 5 ? "center" : "left"}>
									<Typography variant="button">{header}</Typography>
								</TableCell>
							))}
						</TableRow>
					</TableHead>

					<TableBody>
						{rows?.map((row) => (
							<TableNfseRow
								key={row.Installment_id}
								row={row}
								isSelected={selectedRows.includes(row.Installment_id)}
								isSelectable={isNFSelectable(row)}
								onToggleRow={onToggleRowCheckbox}
								onOpenMenu={(e) => handleOpenMenu(e, row)}
							/>
						))}
					</TableBody>
				</Table>

				{selectedRow && (
					<NFMenu
						anchorEl={anchorEl}
						onClose={handleCloseMenu}
						row={selectedRow}
						onClickVisualizarNFSE={() => {}}
						onClickCancelarNFSE={() => {
							handleCloseMenu();
							setTimeout(() => setOpenCancelModal(true), 150);
						}}
						onClickGerarNFSE={(row) => handleGenerateNfse(row.Installment_id)}
						onClickDownloadPDF={handleDownloadNfsePDF}
						onClickRefreshStatus={(row) => handleUpdateStatusNfse(row, refreshList)}
						onClickResendEmail={() => {}}
					/>
				)}

				{selectedRow && (
					<ModalCancelNfse
						open={openCancelModal}
						onClose={() => {
							setOpenCancelModal(false);
							setSelectedRow(null);
						}}
						nfseId={selectedRow?.nfse_id}
						documentGuid={selectedRow?.document_guid}
						refreshList={refreshList}
						name={selectedRow?.tenant_name}
					/>
				)}

				<Box display="flex" justifyContent="flex-end" alignItems="center" mt={2} pr={2}>
					<TablePagination
						component="div"
						count={totalCount}
						page={page}
						rowsPerPage={rowsPerPage}
						onPageChange={(_, newPage) => onPageChange(newPage)}
						onRowsPerPageChange={(e) => onRowsPerPageChange(parseInt(e.target.value, 10))}
						rowsPerPageOptions={[10, 25, 50]}
						labelRowsPerPage="Linhas por página:"
						labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count} registros`}
					/>
				</Box>
			</TableContainer>
		</>
	);
}
